import React from 'react';
import { Clock } from 'lucide-react';
import { formatDate, formatTimeString12Hour } from '../../utils/dateUtils';
import { formatLeaveDays, pluralizeDays } from '../../utils/numberUtils';
import { InfoBadge } from '../UI/InfoBadge';
import { OIL_EXPIRY_DAYS } from '../../constants/leaveTypes';
import { formatOilWorkedHours, OIL_FULL_DAY_MIN_HOURS } from '../../utils/oilHoursCap';

function clockLabel(hhmm) {
  if (!hhmm) return null;
  return formatTimeString12Hour(String(hhmm).slice(0, 5));
}

/**
 * Shared OIL claim work-date rows for staff / RO / HR detail surfaces.
 * Shows clock evidence next to the credited days so reviewers do not
 * have to leave the modal to check attendance.
 */
export default function OilClaimWorkDates({ oilDates, expiryDays = OIL_EXPIRY_DAYS }) {
  if (!Array.isArray(oilDates) || oilDates.length === 0) return null;

  const total = oilDates.reduce((sum, entry) => sum + (parseFloat(entry.days) || 0), 0);

  return (
    <div className="space-y-2">
      {oilDates.map((entry, idx) => {
        const workDate = entry.date || entry.work_date;
        const expiryDate =
          entry.expiry_date ||
          (workDate
            ? new Date(
                new Date(workDate).getTime() + expiryDays * 24 * 60 * 60 * 1000
              )
                .toISOString()
                .split('T')[0]
            : null);
        const hours = entry.verified_hours != null ? Number(entry.verified_hours) : null;
        const inLabel = clockLabel(entry.clock_in);
        const outLabel = clockLabel(entry.clock_out);
        const underFour =
          hours != null && hours < OIL_FULL_DAY_MIN_HOURS && inLabel && outLabel;
        const overCredited = underFour && parseFloat(entry.days) >= 1;

        return (
          <div
            key={`${workDate || 'oil'}-${idx}`}
            className="rounded-lg bg-teal-50 px-3 py-2 text-sm"
          >
            <div className="flex items-center justify-between gap-2">
              <span className="font-medium text-teal-800">{formatDate(workDate)}</span>
              <div className="flex items-center gap-2 flex-shrink-0">
                <span className="text-teal-700">
                  {formatLeaveDays(entry.days)} {pluralizeDays(entry.days)}
                </span>
                {expiryDate && (
                  <InfoBadge color="amber" label={`Exp: ${formatDate(expiryDate)}`} />
                )}
              </div>
            </div>
            {(inLabel || outLabel || hours != null) && (
              <div className="mt-1.5 flex items-start gap-1.5 text-xs text-teal-800">
                <Clock className="w-3.5 h-3.5 text-teal-500 flex-shrink-0 mt-0.5" />
                <span>
                  {inLabel && outLabel
                    ? `${inLabel} – ${outLabel}`
                    : inLabel
                      ? `In ${inLabel}`
                      : outLabel
                        ? `Out ${outLabel}`
                        : 'Attendance recorded'}
                  {hours != null && (
                    <span className="text-teal-600"> · {formatOilWorkedHours(hours)}</span>
                  )}
                  {underFour && (
                    <span className="block text-amber-700 mt-0.5">
                      {overCredited
                        ? `Under ${OIL_FULL_DAY_MIN_HOURS} hours — entitlement is 0.5 day`
                        : `Under ${OIL_FULL_DAY_MIN_HOURS} hours · 0.5 day entitlement`}
                    </span>
                  )}
                </span>
              </div>
            )}
          </div>
        );
      })}
      <div className="flex items-center justify-between pt-2 border-t border-gray-200">
        <span className="text-sm font-medium text-teal-700">Total OIL Days</span>
        <span className="text-sm font-bold text-teal-900">{formatLeaveDays(total)}</span>
      </div>
    </div>
  );
}
